import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ROUTES } from '@/lib/constants';
import { ApoloLogo } from '@/shared/components/ApoloLogo';
import { useValidateResetToken, useConfirmPasswordReset } from '../hooks/usePasswordReset';
function getQueryToken() {
    if (typeof window === 'undefined')
        return null;
    return new URLSearchParams(window.location.search).get('token');
}
export function ResetPasswordPage() {
    const token = useMemo(getQueryToken, []);
    const { data, isLoading, error } = useValidateResetToken(token);
    if (!token) {
        return _jsx(FullPageMessage, { title: "Enlace inv\u00E1lido", body: "Falta el token. Pide al director que te reenv\u00EDe el enlace de restablecimiento." });
    }
    if (isLoading) {
        return _jsx(FullPageMessage, { title: "Comprobando enlace\u2026" });
    }
    if (error) {
        return _jsx(FullPageMessage, { title: titleForError(error), body: bodyForError(error) });
    }
    return _jsx(ResetForm, { token: token, email: data?.email ?? '' });
}
function titleForError(err) {
    switch (err.code) {
        case 'PASSWORD_RESET_TOKEN_EXPIRED':
            return 'Enlace caducado';
        case 'PASSWORD_RESET_TOKEN_USED':
            return 'Enlace ya utilizado';
        default:
            return 'Enlace no válido';
    }
}
function bodyForError(err) {
    switch (err.code) {
        case 'PASSWORD_RESET_TOKEN_EXPIRED':
            return 'El enlace ha caducado (son válidos durante 1 hora). Pide al director que te envíe uno nuevo.';
        case 'PASSWORD_RESET_TOKEN_USED':
            return 'Este enlace ya se usó para cambiar la contraseña. Si necesitas otro, pídeselo al director.';
        default:
            return 'El enlace no corresponde a ninguna solicitud. Comprueba que copiaste la URL completa del email.';
    }
}
function ResetForm({ token, email }) {
    const confirm = useConfirmPasswordReset();
    const [submitError, setSubmitError] = useState(null);
    const [done, setDone] = useState(false);
    const { register, handleSubmit, watch, formState: { errors }, } = useForm();
    async function onSubmit(values) {
        setSubmitError(null);
        try {
            await confirm.mutateAsync({ token, password: values.password });
            setDone(true);
        }
        catch (err) {
            const apiErr = err;
            setSubmitError(messageForError(apiErr));
        }
    }
    if (done) {
        return (_jsx(FullPageMessage, { title: "Contrase\u00F1a actualizada", body: "Ya puedes iniciar sesi\u00F3n con tu nueva contrase\u00F1a.", showLogin: true }));
    }
    return (_jsx("div", { className: "min-h-screen bg-[#0B1220] text-white py-10 px-4 flex items-center justify-center", children: _jsxs("div", { className: "w-full max-w-md", children: [_jsxs("header", { className: "mb-8 text-center", children: [_jsx(ApoloLogo, { size: 44 }), _jsx("h1", { className: "mt-4 text-2xl font-bold tracking-tight", children: "Restablece tu contrase\u00F1a" }), email && (_jsxs("p", { className: "mt-2 text-sm text-white/60", children: ["Cuenta: ", _jsx("span", { className: "font-mono", children: email })] })), _jsx("p", { className: "mt-2 text-xs text-white/50", children: "M\u00EDnimo 8 caracteres, una may\u00FAscula y un n\u00FAmero." })] }), _jsxs("form", { onSubmit: handleSubmit(onSubmit), className: "space-y-4", noValidate: true, children: [_jsx(Field, { label: "Nueva contrase\u00F1a", error: errors.password?.message, children: _jsx("input", { type: "password", autoComplete: "new-password", className: inputCls, ...register('password', {
                                    required: 'La contraseña es obligatoria',
                                    minLength: { value: 8, message: 'Mínimo 8 caracteres' },
                                    validate: {
                                        upper: (v) => /[A-Z]/.test(v) || 'Debe contener al menos una mayúscula',
                                        digit: (v) => /[0-9]/.test(v) || 'Debe contener al menos un número',
                                    },
                                }) }) }), _jsx(Field, { label: "Repite la contrase\u00F1a", error: errors.confirmPassword?.message, children: _jsx("input", { type: "password", autoComplete: "new-password", className: inputCls, ...register('confirmPassword', {
                                    required: 'Repite la contraseña',
                                    validate: (v) => v === watch('password') || 'Las contraseñas no coinciden',
                                }) }) }), submitError && (_jsx("div", { className: "rounded-md border border-red-400/50 bg-red-500/10 px-4 py-3 text-sm text-red-200", children: submitError })), _jsx("button", { type: "submit", disabled: confirm.isPending, className: "w-full px-6 py-3 rounded-md bg-[#053C97] hover:bg-[#0950c8] disabled:opacity-60 font-semibold tracking-tight transition text-sm", children: confirm.isPending ? 'Guardando…' : 'Cambiar contraseña' })] })] }) }));
}
function messageForError(err) {
    switch (err.code) {
        case 'PASSWORD_RESET_TOKEN_EXPIRED':
            return 'El enlace ha caducado mientras rellenabas el formulario. Pide uno nuevo al director.';
        case 'PASSWORD_RESET_TOKEN_USED':
            return 'Este enlace ya fue utilizado.';
        case 'PASSWORD_RESET_TOKEN_INVALID':
            return 'El enlace no es válido.';
        case 'VALIDATION_ERROR':
            return 'La contraseña no cumple los requisitos.';
        default:
            return 'No se pudo cambiar la contraseña. Inténtalo de nuevo en unos minutos.';
    }
}
function FullPageMessage({ title, body, showLogin, }) {
    return (_jsx("div", { className: "min-h-screen flex items-center justify-center px-6 bg-[#0B1220] text-white", children: _jsxs("div", { className: "max-w-md text-center space-y-4", children: [_jsx(ApoloLogo, { size: 48 }), _jsx("h1", { className: "text-2xl font-bold tracking-tight", children: title }), body && _jsx("p", { className: "text-white/70 leading-relaxed", children: body }), (showLogin ?? true) && (_jsx("a", { href: ROUTES.LOGIN, className: "inline-block mt-4 text-sm underline text-white/80 hover:text-white", children: "Ir al panel de acceso" }))] }) }));
}
const inputCls = 'w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#38B6EF] focus:ring-1 focus:ring-[#38B6EF]/40';
function Field({ label, error, children, }) {
    return (_jsxs("label", { className: "block", children: [_jsx("span", { className: "block text-[11px] font-mono uppercase tracking-[0.18em] text-white/55 mb-1.5", children: label }), children, error && _jsx("span", { className: "block mt-1 text-[12px] text-red-300", children: error })] }));
}
//# sourceMappingURL=ResetPasswordPage.js.map